/**
 * Maps human-readable keys to translation IDs for the
 * /tools/score2-calculator SEO landing page. New content (not from Framer
 * XLIFF), so keys carry the `score2_` prefix directly.
 *
 * The page is gated to CH + DE (`page:tools/score2-calculator`), in all three
 * Swiss locales and in German for the DE market, so `de-DE` is held to full
 * parity with this namespace while de-AT, en-NL and nl still exempt it in
 * `SKIP_NAMESPACES_PER_LOCALE` (scripts/lint-mirror-locales.ts). Germany took
 * more than a translation, unlike every other calculator: SCORE2 is
 * recalibrated per CVD risk region, and its `moderate` entry in
 * `SCORE2_REGION_BY_COUNTRY` (lib/score2.ts) is what makes the German page
 * score a German reader rather than a Swiss one. The German copy carries that
 * through — its region section names the moderate region and quotes 7.8%
 * where the Swiss one quotes 6.1% for the same person — and it asks the two
 * lipids in mg/dL, the unit German labs report (hence the four unit keys
 * below).
 *
 * No key in this namespace takes an ICU placeholder. That is deliberate: the
 * band and age-band definitions in `lib/score2.ts` store these keys as `as
 * const` literals, and a single placeholder-bearing key in the namespace would
 * make next-intl's typed `t()` demand a `values` argument at every
 * `t(band.labelKey)` call site (the TS2554 trap documented in the BMI
 * calculator's `bmi.ts`).
 */
export const score2CalculatorKeys = {
  // Metadata
  metaTitle: "score2_metaTitle",
  metaDescription: "score2_metaDescription",

  // Hero
  heading: "score2_heading", // "SCORE2 calculator"
  subheading: "score2_subheading",

  // Calculator widget
  calcTitle: "score2_calcTitle",
  calcSexLabel: "score2_calcSexLabel",
  calcSexFemale: "score2_calcSexFemale",
  calcSexMale: "score2_calcSexMale",
  calcAgeLabel: "score2_calcAgeLabel",
  calcSmokerLabel: "score2_calcSmokerLabel",
  calcSmokerYes: "score2_calcSmokerYes",
  calcSmokerNo: "score2_calcSmokerNo",
  calcSbpLabel: "score2_calcSbpLabel",
  calcSbpHint: "score2_calcSbpHint",
  calcTotalCholLabel: "score2_calcTotalCholLabel",
  calcHdlLabel: "score2_calcHdlLabel",
  calcCholHint: "score2_calcCholHint",
  // The mg/dL twin of the hint above, and the shared switch's label.
  // Rendered only where `feature:tools-mgdl-lipids` allows the market, but
  // present in every catalog: en.json is what types `t()`, so a key missing
  // there is a compile error at the call site rather than a missing string.
  calcCholHintMgDl: "score2_calcCholHintMgDl",
  calcCholUnitLabel: "score2_calcCholUnitLabel",
  calcUnitYears: "score2_calcUnitYears", // "years"
  calcUnitMmhg: "score2_calcUnitMmhg", // "mmHg"
  calcUnitMmol: "score2_calcUnitMmol", // "mmol/L"
  calcUnitMgDl: "score2_calcUnitMgDl", // "mg/dL"
  calcSubmit: "score2_calcSubmit",
  calcReset: "score2_calcReset",
  calcResultLabel: "score2_calcResultLabel", // "Your 10-year risk"
  calcResultSuffix: "score2_calcResultSuffix", // "%"
  calcEmptyState: "score2_calcEmptyState",
  calcOutOfRange: "score2_calcOutOfRange",
  calcAgeOutOfRange: "score2_calcAgeOutOfRange",
  calcHdlAboveTotal: "score2_calcHdlAboveTotal",
  // A mmol/L pair typed while mg/dL is the selected unit. See
  // `looksLikeMmolPerLEntry` (lib/lipid-units.ts) for why that direction
  // needs its own detection: converted, it is low rather than impossible.
  calcCholMmolValue: "score2_calcCholMmolValue",
  calcThresholdsLabel: "score2_calcThresholdsLabel",
  calcScaleCaption: "score2_calcScaleCaption",
  calcEligibilityNote: "score2_calcEligibilityNote",
  calcDisclaimer: "score2_calcDisclaimer",

  // Quiz hand-off, shown in the result panel once Calculate has run
  quizHeading: "score2_quizHeading",
  quizBody: "score2_quizBody",
  quizCta: "score2_quizCta",

  // Risk bands (shared by the result card and the reference table)
  bandLowToModerate: "score2_bandLowToModerate",
  bandHigh: "score2_bandHigh",
  bandVeryHigh: "score2_bandVeryHigh",

  meaningLowToModerate: "score2_meaningLowToModerate",
  meaningHigh: "score2_meaningHigh",
  meaningVeryHigh: "score2_meaningVeryHigh",

  // Age groups the ESC thresholds are cut by
  ageUnder50: "score2_ageUnder50",
  age50to69: "score2_age50to69",
  age70plus: "score2_age70plus",

  // Threshold cells, one per age group x band
  thrUnder50LowToModerate: "score2_thrUnder50LowToModerate",
  thrUnder50High: "score2_thrUnder50High",
  thrUnder50VeryHigh: "score2_thrUnder50VeryHigh",
  thr50to69LowToModerate: "score2_thr50to69LowToModerate",
  thr50to69High: "score2_thr50to69High",
  thr50to69VeryHigh: "score2_thr50to69VeryHigh",
  thr70plusLowToModerate: "score2_thr70plusLowToModerate",
  thr70plusHigh: "score2_thr70plusHigh",
  thr70plusVeryHigh: "score2_thr70plusVeryHigh",

  // Threshold reference table
  tableHeading: "score2_tableHeading",
  tableIntro: "score2_tableIntro",
  tableColAge: "score2_tableColAge",
  tableNote: "score2_tableNote",

  // What SCORE2 is
  whatLabel: "score2_whatLabel",
  whatHeading: "score2_whatHeading",
  whatP1: "score2_whatP1",
  whatP2: "score2_whatP2",
  whatP3: "score2_whatP3",

  // What goes into it
  inputsLabel: "score2_inputsLabel",
  inputsHeading: "score2_inputsHeading",
  inputsIntro: "score2_inputsIntro",
  input1Title: "score2_input1Title",
  input1Body: "score2_input1Body",
  input2Title: "score2_input2Title",
  input2Body: "score2_input2Body",
  input3Title: "score2_input3Title",
  input3Body: "score2_input3Body",
  input4Title: "score2_input4Title",
  input4Body: "score2_input4Body",
  input5Title: "score2_input5Title",
  input5Body: "score2_input5Body",

  // Who it is for, who it is not for
  whoHeading: "score2_whoHeading",
  whoIntro: "score2_whoIntro",
  whoForTitle: "score2_whoForTitle",
  whoFor1: "score2_whoFor1",
  whoFor2: "score2_whoFor2",
  whoFor3: "score2_whoFor3",
  whoNotForTitle: "score2_whoNotForTitle",
  whoNotFor1: "score2_whoNotFor1",
  whoNotFor2: "score2_whoNotFor2",
  whoNotFor3: "score2_whoNotFor3",
  whoNotFor4: "score2_whoNotFor4",
  whoNote: "score2_whoNote",

  // Why the Swiss calibration
  regionHeading: "score2_regionHeading",
  regionP1: "score2_regionP1",
  regionP2: "score2_regionP2",
  regionTipTitle: "score2_regionTipTitle",
  regionTipBody: "score2_regionTipBody",

  // Limitations
  limitsLabel: "score2_limitsLabel",
  limitsHeading: "score2_limitsHeading",
  limitsIntro: "score2_limitsIntro",
  limit1Title: "score2_limit1Title",
  limit1Body: "score2_limit1Body",
  limit2Title: "score2_limit2Title",
  limit2Body: "score2_limit2Body",
  limit3Title: "score2_limit3Title",
  limit3Body: "score2_limit3Body",
  limit4Title: "score2_limit4Title",
  limit4Body: "score2_limit4Body",

  // Membership section
  memLabel: "score2_memLabel",
  memHeading: "score2_memHeading",
  memBody: "score2_memBody",
  memFeature1: "score2_memFeature1",
  memFeature2: "score2_memFeature2",
  memFeature3: "score2_memFeature3",
  memFeature4: "score2_memFeature4",
  memFeature5: "score2_memFeature5",
  memCtaPrimary: "score2_memCtaPrimary",
  memCtaSecondary: "score2_memCtaSecondary",
  memFootnote: "score2_memFootnote",

  // FAQ fallback (used until page-tagged FAQs exist in the CMS)
  faqHeading: "score2_faqHeading",
  faqQ1: "score2_faqQ1",
  faqA1: "score2_faqA1",
  faqQ2: "score2_faqQ2",
  faqA2: "score2_faqA2",
  faqQ3: "score2_faqQ3",
  faqA3: "score2_faqA3",
  faqQ4: "score2_faqQ4",
  faqA4: "score2_faqA4",
  faqQ5: "score2_faqQ5",
  faqA5: "score2_faqA5",
  faqQ6: "score2_faqQ6",
  faqA6: "score2_faqA6",

  // Closing CTA
  ctaHeading: "score2_ctaHeading",
  ctaBody: "score2_ctaBody",
  ctaPrimary: "score2_ctaPrimary",
  ctaSecondary: "score2_ctaSecondary",

  // Legal / medical
  sourceLabel: "score2_sourceLabel",
  sourceBody: "score2_sourceBody",
  disclaimer: "score2_disclaimer",
} as const
